import { DeepPartial } from 'chart.js/types/utils'
import { ChartType, ChartTypeRegistry, CoreChartOptions, DatasetChartOptions, DecimationOptions, ElementChartOptions, FillerOptions, LegendItem, LegendOptions, PluginChartOptions, PluginOptionsByType, ScaleChartOptions, TitleOptions, TooltipOptions } from 'chart.js'

const getOrCreateLegendList = (chart, id) => {
    const legendContainer = document.getElementById(id)
    let listContainer = legendContainer.querySelector('ul')

    if (!listContainer) {
        listContainer = document.createElement('ul')
        listContainer.style.display = 'flex'
        listContainer.style.flexDirection = 'column'
        listContainer.style.margin = '0'
        listContainer.style.padding = '0'
        listContainer.style.gap = '5px'
        listContainer.style.height = '100%'
        listContainer.style.overflowY = 'auto'
        listContainer.style.justifyContent = 'center'

        legendContainer.appendChild(listContainer)
    }

    return listContainer
}

export default {
    id: 'htmlLegend',
    afterUpdate(chart, args, options) {
        const ul = getOrCreateLegendList(chart, options.containerID)

        // Remove old legend items
        while (ul.firstChild) {
            ul.firstChild.remove()
        }

        // Reuse the built-in legendItems generator
        const items = chart.options.plugins.legend.labels.generateLabels(chart)

        items.forEach(item => {
            if (item.index !== options.activeIndex) {
                item.hidden = true
            }

            const li = document.createElement('li')
            li.style.alignItems = 'center'
            li.style.cursor = 'pointer'
            li.style.display = 'flex'
            li.style.flexDirection = 'row'
            li.style.marginLeft = '10px'

            li.onclick = () => {
                const {type} = chart.config
                if (type === 'pie' || type === 'doughnut') {
                    // Pie and doughnut charts only have a single dataset and visibility is per item
                    chart.toggleDataVisibility(item.index)

                    if (options.onClick) {
                        options.onClick(item)
                    }
                } else {
                    chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex))

                    if (options.onClick) {
                        options.onClick(item)
                    }
                }
                chart.update()
            }

            // Color box
            const boxSpan = document.createElement('span')
            boxSpan.style.background = item.fillStyle
            boxSpan.style.borderColor = item.strokeStyle
            boxSpan.style.borderWidth = item.lineWidth + 'px'
            boxSpan.style.display = 'inline-block'
            boxSpan.style.minHeight = '13px'
            boxSpan.style.marginRight = '10px'
            boxSpan.style.minWidth = '42px'

            // Text
            const textContainer = document.createElement('p')
            textContainer.style.color = '#666'
            textContainer.style.fontSize = '.75rem'
            textContainer.style.margin = '0'
            textContainer.style.padding = '0'

            const text = document.createTextNode(item.text)
            textContainer.appendChild(text)

            li.appendChild(boxSpan)
            li.appendChild(textContainer)
            ul.appendChild(li)
        })
    },
}

export interface PluginOptionsByTypeWithHtmlLegend<TType extends ChartType> {
    decimation: DecimationOptions
    filler: FillerOptions
    legend: LegendOptions
    htmlLegend: {
        containerID: string,
        activeIndex: any,
        onClick: (item: LegendItem) => void,
    }
    title: TitleOptions
    tooltip: TooltipOptions<TType>
}

export interface PluginChartOptionsWithHtmlLegend<TType extends ChartType> {
    plugins: PluginOptionsByTypeWithHtmlLegend<TType>
}

export type ChartOptionsWithHtmlLegend<TType extends ChartType = ChartType> = DeepPartial<
    CoreChartOptions<TType> &
    ElementChartOptions<TType> &
    PluginChartOptionsWithHtmlLegend<TType> &
    DatasetChartOptions<TType> &
    ScaleChartOptions<TType> &
    ChartTypeRegistry[TType]['chartOptions']
>
